// Owner-Bereich → Neuigkeiten: Update-Hinweise schreiben, veröffentlichen,
// per Mail verschicken.
//
// Drei getrennte Schritte, damit nichts aus Versehen rausgeht:
//   Entwurf          nur hier sichtbar, frei änderbar
//   Veröffentlichen  erscheint beim nächsten Anmelden als „Was ist neu"
//   Per Mail         nach Bestätigung der Empfängerzahl, einmal je Neuigkeit
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, datum } from '../../../gemeinsam/lib/daten';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import { Abzeichen, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Punkt = { titel: string; text: string };
type Neuigkeit = {
  id: string; version: string; titel: string; einleitung: string; punkte: Punkt[];
  erstelltAm: string; veroeffentlichtAm: string | null;
  versand: null | { status: 'laeuft' | 'fertig' | 'abgebrochen'; empfaenger: number; gesendet: number; fehler: number; gestartetAm: string; beendetAm?: string };
  offeneEmpfaenger: number | null;
};
type Antwort = { neuigkeiten: Neuigkeit[]; mailBereit: boolean };
type Entwurf = { id?: string; version: string; titel: string; einleitung: string; punkte: Punkt[] };

const LEER = (): Entwurf => ({ version: '', titel: '', einleitung: '', punkte: [{ titel: '', text: '' }, { titel: '', text: '' }] });

export default function NeuigkeitenVerwaltung() {
  const { daten, laedt, fehler: ladeFehler, neuLaden } = useDaten<Antwort>('/neuigkeiten/verwaltung');
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function ausfuehren(f: () => Promise<unknown>, text: string) {
    setFehler(null); setSendet(true);
    try { await f(); meldungZeigen(text); await neuLaden(); return true; }
    catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Hat nicht geklappt.'); return false; }
    finally { setSendet(false); }
  }

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (!entwurf) return;
    const daten = { ...entwurf, punkte: entwurf.punkte.filter(p => p.titel.trim()) };
    const geklappt = await ausfuehren(() => entwurf.id
      ? api(`/neuigkeiten/verwaltung/${entwurf.id}`, { methode: 'PUT', daten })
      : api('/neuigkeiten/verwaltung', { methode: 'POST', daten }), 'Entwurf gespeichert.');
    if (geklappt) setEntwurf(null);
  }

  function veroeffentlichen(n: Neuigkeit) {
    if (!confirm(`„${n.titel}" veröffentlichen?\n\nAlle Benutzer sehen es beim nächsten Anmelden als „Was ist neu". Danach lässt es sich nicht mehr löschen.`)) return;
    void ausfuehren(() => api(`/neuigkeiten/verwaltung/${n.id}/veroeffentlichen`, { methode: 'POST' }), 'Veröffentlicht — erscheint beim nächsten Anmelden.');
  }

  function verschicken(n: Neuigkeit) {
    const anzahl = n.offeneEmpfaenger ?? 0;
    if (!confirm(`„${n.titel}" jetzt an ${anzahl} Empfänger per E-Mail schicken?\n\nNur bestätigte Adressen, die Neuigkeiten nicht abbestellt haben. Jede Mail enthält einen Abmeldelink. Das lässt sich nicht zurückholen.`)) return;
    void ausfuehren(() => api(`/neuigkeiten/verwaltung/${n.id}/mail`, { methode: 'POST', daten: { bestaetigt: anzahl } }),
      `Versand an ${anzahl} Empfänger gestartet.`);
  }

  function loeschen(n: Neuigkeit) {
    if (!confirm(`Entwurf „${n.titel}" löschen?`)) return;
    void ausfuehren(() => api(`/neuigkeiten/verwaltung/${n.id}`, { methode: 'DELETE' }), 'Entwurf gelöscht.');
  }

  const setzePunkt = (i: number, feld: keyof Punkt, wert: string) =>
    setEntwurf(e => e && ({ ...e, punkte: e.punkte.map((p, k) => k === i ? { ...p, [feld]: wert } : p) }));

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Neuigkeiten</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Nach jedem Update: was neu ist. Erscheint in der App beim nächsten Anmelden und auf Wunsch per Mail.
          </p>
        </div>
        {!entwurf && <Knopf variante="primaer" onClick={() => setEntwurf(LEER())}>Neue Neuigkeit</Knopf>}
      </div>

      {(fehler || ladeFehler) && <Hinweis art="fehler">{fehler || ladeFehler}</Hinweis>}
      {daten && !daten.mailBereit && <Hinweis art="warnung">Auf diesem Server ist kein Mailversand eingerichtet — veröffentlichen geht, verschicken nicht.</Hinweis>}

      {entwurf && (
        <Karte>
          <KartenKopf titel={entwurf.id ? 'Entwurf bearbeiten' : 'Neue Neuigkeit'} hinweis="Kurz und konkret: was kann man jetzt, was vorher nicht ging." />
          <form onSubmit={speichern} className="space-y-4 p-5">
            <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
              <Feld label="Version" placeholder="2.4" required value={entwurf.version}
                    onChange={e => setEntwurf(x => x && ({ ...x, version: e.target.value }))} />
              <Feld label="Titel" placeholder="Rechnungen im Fenster, GiroCode überall" required value={entwurf.titel}
                    onChange={e => setEntwurf(x => x && ({ ...x, titel: e.target.value }))} />
            </div>
            <Feld label="Einleitung (optional)" value={entwurf.einleitung}
                  onChange={e => setEntwurf(x => x && ({ ...x, einleitung: e.target.value }))} />
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Was ist neu</p>
              {entwurf.punkte.map((p, i) => (
                <div key={i} className="grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[1fr_1.4fr] dark:border-slate-800">
                  <Feld label={`Punkt ${i + 1}`} value={p.titel} onChange={e => setzePunkt(i, 'titel', e.target.value)} />
                  <Feld label="Erklärung (optional)" value={p.text} onChange={e => setzePunkt(i, 'text', e.target.value)} />
                </div>
              ))}
              {entwurf.punkte.length < 15 && (
                <Knopf type="button" variante="still" onClick={() => setEntwurf(x => x && ({ ...x, punkte: [...x.punkte, { titel: '', text: '' }] }))}>
                  + Punkt
                </Knopf>
              )}
            </div>
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
              <Knopf type="submit" variante="primaer" laedt={sendet}>Entwurf speichern</Knopf>
              <Knopf type="button" onClick={() => { setEntwurf(null); setFehler(null); }}>Abbrechen</Knopf>
            </div>
          </form>
        </Karte>
      )}

      <Karte>
        {laedt && !daten ? <div className="flex justify-center py-12"><Spinner /></div>
          : !daten?.neuigkeiten.length ? (
            <LeerZustand titel="Noch keine Neuigkeiten" text="Nach dem nächsten Update hier festhalten, was neu ist." />
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {daten.neuigkeiten.map(n => (
                <li key={n.id} className="space-y-3 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{n.version} · {n.titel}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {n.punkte.length} Punkte · angelegt {datum(n.erstelltAm)}
                        {n.veroeffentlichtAm && ` · veröffentlicht ${datum(n.veroeffentlichtAm)}`}
                      </p>
                    </div>
                    <Abzeichen art={n.veroeffentlichtAm ? 'gruen' : 'neutral'}>{n.veroeffentlichtAm ? 'veröffentlicht' : 'Entwurf'}</Abzeichen>
                  </div>
                  <ul className="list-inside list-disc text-sm text-slate-700 dark:text-slate-300">
                    {n.punkte.map((p, i) => <li key={i}>{p.titel}</li>)}
                  </ul>
                  {n.versand && (
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Mail: {n.versand.status === 'laeuft' ? 'läuft' : n.versand.status === 'abgebrochen' ? 'abgebrochen (fortsetzbar)' : 'fertig'}
                      {' · '}{n.versand.gesendet} von {n.versand.empfaenger} verschickt
                      {n.versand.fehler > 0 && <span className="text-red-600"> · {n.versand.fehler} fehlgeschlagen</span>}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {!n.versand?.gesendet && (
                      <Knopf variante="still" onClick={() => setEntwurf({ id: n.id, version: n.version, titel: n.titel, einleitung: n.einleitung, punkte: n.punkte })}>
                        Bearbeiten
                      </Knopf>
                    )}
                    {!n.veroeffentlichtAm && <Knopf variante="primaer" onClick={() => veroeffentlichen(n)}>Veröffentlichen</Knopf>}
                    {n.veroeffentlichtAm && daten.mailBereit && (n.offeneEmpfaenger ?? 0) > 0 && n.versand?.status !== 'laeuft' && (
                      <Knopf variante="primaer" laedt={sendet} onClick={() => verschicken(n)}>
                        {n.versand ? 'Versand fortsetzen' : 'Per Mail verschicken'} ({n.offeneEmpfaenger})
                      </Knopf>
                    )}
                    {n.versand?.status === 'laeuft' && <Knopf onClick={() => neuLaden()}>Stand aktualisieren</Knopf>}
                    {!n.veroeffentlichtAm && <Knopf variante="gefahr" onClick={() => loeschen(n)}>Löschen</Knopf>}
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Karte>
    </div>
  );
}
